import { isEmpty, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';
import { PROCESSING_STATUS_LABEL } from '../../../domain/customer-utility-bill/interface';
import {
    CustomerUtilityBillFileStatus,
    FILE_PROCESSING_STATUSES,
    billSourceLabel,
    formatCustomerName,
} from '../../../domain/customer-utility-bill-file/interface';
import { BillFilesFilterKeys, TBillFilesFilterKey } from './BillFilesSidebarFilters';

// Distinct option values loaded from GET /filters/options/customer-utility-bill-file/{sources|customers}.
export interface IBillFilesFilterOptions {
    sources: string[];
    customers: string[];
}

export const transformBillFilesSavedFilterToUser = (filter: ISavedFilter['filter_values']): Record<TBillFilesFilterKey, string> => {
    const result: Record<TBillFilesFilterKey, string> = {} as Record<TBillFilesFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as empty chips
        if (isUndefined(value) || isNull(value)) continue;

        const values: string[] = (Array.isArray(value) ? value : [value]).map(String);

        switch (key) {
            case BillFilesFilterKeys.source:
                result[key] = values.map(billSourceLabel).join(', ');
                break;
            case BillFilesFilterKeys.customer:
                result[key] = values.map(formatCustomerName).join(', ');
                break;
            case BillFilesFilterKeys.status:
                result[key] = values.map(val => PROCESSING_STATUS_LABEL[val as CustomerUtilityBillFileStatus] ?? val).join(', ');
                break;
        }
    }

    return result;
};

// Returns the keys whose saved values no longer exist in the current filter options (shown as "outdated").
// Options that haven't loaded yet (empty list) are not treated as outdated.
export const checkBillFilesFilterInvalidValues = (filter: ISavedFilter['filter_values'], options: IBillFilesFilterOptions) => {
    const invalidFields: string[] = [];

    const isOutdated = (value: unknown, validOptionValues: string[]) =>
        Array.isArray(value) && validOptionValues.length > 0 && value.some(val => !validOptionValues.includes(val));

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case BillFilesFilterKeys.source:
                if (isOutdated(value, options.sources)) invalidFields.push(key);
                break;
            case BillFilesFilterKeys.customer:
                if (isOutdated(value, options.customers)) invalidFields.push(key);
                break;
            case BillFilesFilterKeys.status:
                if (isOutdated(value, FILE_PROCESSING_STATUSES)) invalidFields.push(key);
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'string') return value.trim() !== '';

        return false;
    }).length;
}
